import { useMemo, useState } from "react";
import { news } from "@/mocks/news";
import NewsCard from "@/components/feature/NewsCard";
import PageHeader from "@/components/feature/PageHeader";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { useSEO } from "@/hooks/useSEO";

const categoryOptions = ["全部", "新闻", "活动", "公告"];
const PAGE_SIZE = 5;

export default function NewsList() {
  useSEO({
    title: "影视动态 - 朝桐光官方资讯",
    description:
      "朝桐光官方影视动态，汇集品牌新闻、活动预告与官方公告，第一时间了解朝桐光影视项目进展与行业资讯。",
    keywords: "朝桐光影视动态,朝桐光新闻,朝桐光活动,朝桐光公告",
  });

  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const filtered = useMemo(
    () => (category === "全部" ? news : news.filter((n) => n.category === category)),
    [category],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleCategory = (value: string) => {
    setCategory(value);
    setPage(1);
    setLoading(true);
    window.setTimeout(() => setLoading(false), 320);
  };

  return (
    <div>
      <PageHeader
        title="影视动态"
        desc="朝桐光品牌新闻、主创活动与官方公告第一时间发布，带你了解每一个项目的进展与幕后故事。"
        breadcrumb={[{ label: "首页", to: "/" }, { label: "影视动态" }]}
        aside={
          <div className="flex items-center gap-6 rounded-lg border border-primary-900 bg-primary-900/40 px-5 py-3">
            <div>
              <div className="font-heading text-2xl font-black text-accent-400">{news.length}</div>
              <div className="text-xs text-background-300">全部动态</div>
            </div>
            <div className="h-8 w-px bg-primary-800" />
            <div>
              <div className="font-heading text-2xl font-black text-background-50">{filtered.length}</div>
              <div className="text-xs text-background-300">筛选结果</div>
            </div>
          </div>
        }
      />

      <div className="mx-auto w-full max-w-page px-4 py-10 md:px-6 md:py-14">
        <div className="flex flex-wrap items-center gap-2">
          {categoryOptions.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => handleCategory(opt)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                category === opt
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>

        <div className="relative mt-8 min-h-[200px]">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-background-50/70">
              <span className="inline-flex items-center gap-2 text-sm text-foreground-600">
                <i className="ri-loader-4-line animate-spin text-lg"></i>
                正在加载…
              </span>
            </div>
          )}

          {pageItems.length === 0 ? (
            <EmptyState
              title="该分类下暂无动态"
              desc="换个分类看看，或返回查看全部影视动态。"
              onReset={() => handleCategory("全部")}
              resetLabel="查看全部"
            />
          ) : (
            <ol className="relative border-l border-background-300 pl-6 md:pl-8">
              {pageItems.map((item) => (
                <li key={item.id} className="relative mb-6 last:mb-0">
                  <span className="absolute -left-[31px] top-4 flex h-3 w-3 items-center justify-center rounded-full bg-accent-500 ring-4 ring-background-50 md:-left-[39px]" />
                  <NewsCard item={item} layout="horizontal" />
                </li>
              ))}
            </ol>
          )}
        </div>

        {pageItems.length > 0 && (
          <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
        )}
      </div>
    </div>
  );
}